import React, { useEffect } from 'react'
import { css, StyleSheet } from 'aphrodite'
import { Handle, Position, useUpdateNodeInternals } from 'reactflow'
import { AppTheme } from '../../../../styles/AppTheme'
import { ChatbotNodeHeader, chatbotNodeIcon } from './ChatbotNodeHeader'
import { ChatbotTargetHandle } from './ChatbotTargetHandle'
import { HANDLE_OUT, NODE_TYPE_MESSAGE, getOutgoingHandles, normalizeNodeData } from './chatbotGraph'
import { WhatsappDocumentCard, formatWhatsappText } from './chatbotBubblePreview'
import { ChatbotBranchHandles, ChatbotDefaultHandle, partitionBranchHandles } from './ChatbotBranchHandles'

const HANDLE_STYLE = {
  width: 10,
  height: 10,
  background: AppTheme.primaryColor,
  border: '2px solid #ffffff',
}

const ROW_HANDLE_STYLE = Object.assign({}, HANDLE_STYLE, {
  top: '50%',
  right: 0,
  transform: 'translate(50%, -50%)',
})

function truncate(text, max) {
  const s = String(text || '').trim()
  if (!s) return ''
  if (s.length <= max) return s
  return s.slice(0, max) + '…'
}

function HeaderMedia(props) {
  const data = props.data || {}
  const headerType = data.headerType || 'NONE'
  if (headerType === 'TEXT') {
    const text = String(data.headerText || '').trim()
    if (!text) return null
    return <div className={css(Styles.headerText)}>{formatWhatsappText(text)}</div>
  }
  if (headerType === 'IMAGE') {
    if (!data.mediaLink) {
      return <div className={css(Styles.mediaPlaceholder)}>No image</div>
    }
    return (
      <div className={css(Styles.mediaBleed)}>
        <img src={data.mediaLink} alt='Message' className={css(Styles.mediaPreview)} />
      </div>
    )
  }
  if (headerType === 'VIDEO') {
    if (!data.mediaLink) {
      return <div className={css(Styles.mediaPlaceholder)}>No video</div>
    }
    return (
      <div className={css(Styles.mediaBleed)}>
        <video
          src={data.mediaLink}
          controls
          controlsList='nodownload noplaybackrate'
          className={css(Styles.mediaVideo) + ' nodrag nopan'}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        />
      </div>
    )
  }
  if (headerType === 'DOCUMENT') {
    if (!data.media && !data.mediaLink) {
      return <div className={css(Styles.mediaPlaceholder)}>No document</div>
    }
    return <WhatsappDocumentCard url={data.mediaLink || data.media} fileName={data.fileName} />
  }
  return null
}

function actionButtonIcon(type) {
  const t = String(type || '').toUpperCase()
  if (t === 'URL') return 'open_in_new'
  if (t === 'FLOW') return 'assignment'
  return 'reply'
}

function ActionButtonsOnBubble(props) {
  const buttons = (props.buttons || []).filter(b => String((b && b.type) || '').toUpperCase() === 'URL')
  if (!buttons.length) return null
  return (
    <div>
      {buttons.map((b, i) => (
        <div key={b.id || i} className={css(Styles.listCta)}>
          <i className={'material-icons-outlined ' + css(Styles.listCtaIcon)}>{actionButtonIcon(b.type)}</i>
          <span>{truncate(b.text, 24) || b.type}</span>
        </div>
      ))}
    </div>
  )
}

function MessageBody(props) {
  const data = props.data || {}
  const bodyText = String(data.bodyText || '')
  const footerText = truncate(data.footerText, 40)
  const iType = data.interactivityType || 'NONE'
  const headerType = data.headerType || 'NONE'
  const bleedMedia = (headerType === 'IMAGE' || headerType === 'VIDEO') && !!data.mediaLink
  return (
    <div>
      {bleedMedia ? <HeaderMedia data={data} /> : null}
      <div className={css(Styles.body)}>
        {!bleedMedia ? <HeaderMedia data={data} /> : null}
        <div className={css(Styles.preview)}>{bodyText.trim() ? formatWhatsappText(bodyText) : 'No text'}</div>
        {footerText ? <div className={css(Styles.footer)}>{footerText}</div> : null}
        {iType === 'LIST' ? (
          <div className={css(Styles.listCta)}>
            <i className={'material-icons-outlined ' + css(Styles.listCtaIcon)}>list_alt</i>
            <span>{truncate(data.listLabel, 24) || 'List'}</span>
          </div>
        ) : null}
        {iType === 'BUTTON' ? <ActionButtonsOnBubble buttons={data.buttons} /> : null}
      </div>
    </div>
  )
}

export function ChatbotMessageNode(props) {
  const raw = props.data || {}
  const data = normalizeNodeData(raw)
  data.isStartNode = !!raw.isStartNode
  data.onDelete = raw.onDelete
  data.onDuplicate = raw.onDuplicate
  data.onSetStartNode = raw.onSetStartNode
  data.defaultEnabled = !!raw.defaultEnabled
  const iType = data.interactivityType || 'NONE'
  const handles = getOutgoingHandles(data)
  const showOutHandle = handles.length === 1 && handles[0].id === HANDLE_OUT
  const { options, fallback } = partitionBranchHandles(showOutHandle ? [] : handles)
  const updateNodeInternals = useUpdateNodeInternals()
  const handleOrder = handles.map(h => h.id).join('|')
  useEffect(() => {
    updateNodeInternals(props.id)
  }, [props.id, handleOrder, updateNodeInternals])
  return (
    <div className={css(Styles.card, props.selected && Styles.cardSelected)}>
      <ChatbotTargetHandle hidden={!!data.isStartNode} style={HANDLE_STYLE} />
      <ChatbotNodeHeader
        title='Send Message'
        icon={chatbotNodeIcon(NODE_TYPE_MESSAGE)}
        isStartNode={data.isStartNode}
        onDelete={data.onDelete}
        onDuplicate={data.onDuplicate}
        onSetStartNode={data.onSetStartNode}
        onToggleDefault={options.length ? raw.onToggleDefault : undefined}
        defaultEnabled={!!fallback}
      />
      <div className={css(Styles.content, fallback && Styles.contentFlush)}>
        <MessageBody data={data} />
        {showOutHandle ? (
          <Handle
            type='source'
            position={Position.Right}
            id={HANDLE_OUT}
            style={HANDLE_STYLE}
          />
        ) : (
          <ChatbotBranchHandles handles={options} interactivityType={iType} handleStyle={ROW_HANDLE_STYLE} />
        )}
      </div>
      <ChatbotDefaultHandle handle={fallback} handleStyle={ROW_HANDLE_STYLE} />
    </div>
  )
}

const Styles = StyleSheet.create({
  card: {
    width: 240,
    backgroundColor: AppTheme.secondaryBg,
    border: '1px solid ' + AppTheme.borderColor,
    borderRadius: AppTheme.borderRadius,
    overflow: 'visible',
    boxSizing: 'border-box',
  },
  cardSelected: {
    borderColor: AppTheme.primaryColor,
    boxShadow: '0 0 0 3px ' + AppTheme.primaryColor + '33',
  },
  content: {
    backgroundColor: '#e7ffdb',
    color: '#111b21',
    borderBottomLeftRadius: AppTheme.borderRadius,
    borderBottomRightRadius: AppTheme.borderRadius,
    overflow: 'visible',
  },
  contentFlush: {
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
  },
  body: {
    padding: '10px 12px 12px',
    textAlign: 'left',
  },
  headerText: {
    fontSize: 13,
    fontWeight: 700,
    lineHeight: '18px',
    color: '#111b21',
    marginBottom: 6,
    wordBreak: 'break-word',
    whiteSpace: 'pre-wrap',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  preview: {
    fontSize: 13,
    lineHeight: '18px',
    color: '#111b21',
    minHeight: 18,
    wordBreak: 'break-word',
    whiteSpace: 'pre-wrap',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  footer: {
    marginTop: 4,
    fontSize: 11,
    lineHeight: '14px',
    color: '#667781',
    wordBreak: 'break-word',
  },
  mediaBleed: {
    width: '100%',
    overflow: 'hidden',
  },
  mediaPreview: {
    width: '100%',
    height: 'auto',
    maxHeight: 140,
    objectFit: 'cover',
    display: 'block',
  },
  mediaVideo: {
    width: '100%',
    height: 'auto',
    maxHeight: 140,
    display: 'block',
    backgroundColor: '#000',
  },
  mediaPlaceholder: {
    width: '100%',
    minHeight: 72,
    borderRadius: 6,
    backgroundColor: '#e4e6eb',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 12,
    color: '#6B7280',
    marginBottom: 6,
  },
  listCta: {
    marginTop: 8,
    paddingTop: 8,
    borderTop: '1px solid #d9efce',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    fontSize: 14,
    lineHeight: '20px',
    fontWeight: 700,
    color: '#027eb5',
  },
  listCtaIcon: {
    fontSize: 16,
    lineHeight: 1,
    color: 'inherit',
  },
})
